import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BiosecurityPermit, BiosecurityRiskRule, Page, QuarantineBooking, QuarantineFacility } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const IMPORT_CATEGORIES = ['LIVE_PLANT', 'PLANT_PRODUCE', 'SEEDS_PROPAGATIVE', 'SOIL_GROWING_MEDIUM', 'LIVE_ANIMAL', 'ANIMAL_PRODUCT', 'BIOLOGICAL_CONTROL_AGENT', 'VETERINARY_BIOLOGICAL'] as const;
const RISK_CATEGORIES = ['LOW', 'MEDIUM', 'HIGH', 'PROHIBITED'] as const;
const FACILITY_KINDS = ['PLANT', 'ANIMAL', 'MIXED'] as const;

type Tab = 'permits' | 'facilities' | 'risk-rules';

/** M33 Agriculture — bio-security: import-permit risk matrix, mandatory quarantine for HIGH-risk consignments, config catalogues. */
export function Biosecurity() {
  const [tab, setTab] = useState<Tab>('permits');
  return (
    <div>
      <PageHeader title="Bio-Security & Quarantine" subtitle="Plant/animal/product import permits, risk matrix, and quarantine clearance (M33)." />
      <TabStrip
        tabs={[
          { key: 'permits', label: 'Import Permits' },
          { key: 'facilities', label: 'Quarantine Facilities' },
          { key: 'risk-rules', label: 'Risk Matrix' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'permits' ? <PermitsTab /> : tab === 'facilities' ? <FacilitiesTab /> : <RiskRulesTab />}
    </div>
  );
}

// ── Permits ───────────────────────────────────────────────────────────────

function PermitsTab() {
  const { persona } = useAuth();
  const isBioOfficer = persona ? persona.roles.includes('BIOSECURITY_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/biosecurity-permits?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<BiosecurityPermit>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Permit no, applicant, origin…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'RISK_ASSESSED', 'QUARANTINE_REQUIRED', 'QUARANTINE_CLEARED', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'HOLD', 'WITHDRAWN', 'REVOKED', 'EXPIRED'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + New import permit
        </button>
      </div>
      {!isBioOfficer ? <RoleNotice roles={['BIOSECURITY_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No biosecurity permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Permit No</th>
                <th className="table-head">Category</th>
                <th className="table-head">Origin</th>
                <th className="table-head">Risk</th>
                <th className="table-head">Due by (48h SLA)</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.permitNo}</td>
                  <td className="table-cell text-xs">{p.importCategory.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{p.originCountry}</td>
                  <td className="table-cell text-xs">{p.riskCategory ? <StatusBadge status={p.riskCategory} /> : '—'}</td>
                  <td className={`table-cell text-xs ${p.slaBreached ? 'font-semibold text-rose-600' : 'text-slate-500'}`}>{formatDateTime(p.dueBy)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <PermitDetailModal
          id={selectedId}
          isBioOfficer={isBioOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewPermitModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({ id, isBioOfficer, onClose, onChanged }: { id: string; isBioOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const pq = useApiQuery<BiosecurityPermit>(`/api/v1/m33/biosecurity-permits/${id}`);
  const bookingsQ = useApiQuery<{ items: QuarantineBooking[] }>(`/api/v1/m33/biosecurity-permits/${id}/quarantine-bookings`);
  const facilitiesQ = useApiQuery<Page<QuarantineFacility>>('/api/v1/m33/quarantine-facilities?limit=50');
  const p = pq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDecision, setShowDecision] = useState<'APPROVE' | 'REJECT' | 'HOLD' | 'revoke' | null>(null);
  const [note, setNote] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [facilityId, setFacilityId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [outcomeFor, setOutcomeFor] = useState<string | null>(null);
  const [findings, setFindings] = useState('');
  const [outcome, setOutcome] = useState<'PASS' | 'FAIL'>('PASS');

  const refreshAll = () => {
    pq.reload();
    bookingsQ.reload();
    onChanged();
  };
  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refreshAll();
      setShowDecision(null);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const withdraw = () => call(() => api.post(`/api/v1/m33/biosecurity-permits/${id}/withdraw`));
  const confirmDecision = () =>
    call(() => {
      if (showDecision === 'revoke') return api.post(`/api/v1/m33/biosecurity-permits/${id}/revoke`, { reason: note });
      return api.post(`/api/v1/m33/biosecurity-permits/${id}/review`, {
        decision: showDecision,
        note: showDecision === 'APPROVE' ? note || undefined : undefined,
        rejectionReason: showDecision === 'REJECT' ? note : undefined,
        holdReason: showDecision === 'HOLD' ? note : undefined,
      });
    });
  const scheduleBooking = () =>
    call(() =>
      api.post(`/api/v1/m33/biosecurity-permits/${id}/quarantine-bookings`, { facilityId, scheduledAt: new Date(scheduledAt).toISOString() }),
    ).then(() => setShowBooking(false));
  const completeBooking = (bookingId: string) =>
    call(() => api.post(`/api/v1/m33/quarantine-bookings/${bookingId}/complete`, { outcome, findings })).then(() => setOutcomeFor(null));
  const cancelBooking = (bookingId: string) => call(() => api.post(`/api/v1/m33/quarantine-bookings/${bookingId}/cancel`, { reason: note || 'Cancelled by officer' }));

  return (
    <Modal title={p ? p.permitNo : 'Biosecurity permit'} onClose={onClose} wide>
      {pq.error ? <ErrorNote message={pq.error} /> : null}
      {pq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Applicant" value={`${p.applicantType}:${p.applicantId}`} mono />
            <Detail label="Category" value={p.importCategory.replaceAll('_', ' ')} />
            <Detail label="Origin" value={p.originCountry} />
            <Detail label="Purpose" value={p.purpose} />
            <Detail label="Risk category" value={p.riskCategory ? <StatusBadge status={p.riskCategory} /> : '—'} />
            <Detail label="Due by" value={formatDateTime(p.dueBy)} />
            <Detail label="SLA breached" value={p.slaBreached ? 'Yes' : 'No'} />
            <Detail label="Issued" value={formatDateTime(p.issuedAt)} />
          </div>
          <Detail label="Commodity description" value={p.commodityDescription} />
          {p.riskNotes ? <Detail label="Risk notes" value={p.riskNotes} /> : null}
          {p.rejectionReason ? <ErrorNote message={`Rejected: ${p.rejectionReason}`} /> : null}
          {p.holdReason ? <ErrorNote message={`On hold: ${p.holdReason}`} /> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}

          {p.riskCategory === 'HIGH' ? (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="field-label !mb-0">Quarantine bookings</div>
                {isBioOfficer && (p.status === 'QUARANTINE_REQUIRED' || p.status === 'RISK_ASSESSED') ? (
                  <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setShowBooking((s) => !s)}>
                    + Schedule booking
                  </button>
                ) : null}
              </div>
              {showBooking ? (
                <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Facility">
                    <select className="field-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
                      <option value="">Select facility…</option>
                      {(facilitiesQ.data?.items ?? []).map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} ({f.kind})
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Scheduled at">
                    <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                  </Field>
                  <button className="btn-primary" disabled={busy || !facilityId || !scheduledAt} onClick={scheduleBooking}>
                    Schedule
                  </button>
                </div>
              ) : null}
              {bookingsQ.loading ? (
                <Spinner />
              ) : (bookingsQ.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-slate-400">No quarantine bookings yet.</p>
              ) : (
                <div className="space-y-2">
                  {bookingsQ.data!.items.map((b) => (
                    <div key={b.id} className="rounded-lg border border-slate-200 p-2.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs">{b.bookingRef}</span>
                        <StatusBadge status={b.status} />
                      </div>
                      <div className="mt-1 text-xs text-slate-500">Scheduled {formatDateTime(b.scheduledAt)}</div>
                      {b.outcome ? <div className="mt-1 text-xs">Outcome: <StatusBadge status={b.outcome} /> — {b.findings}</div> : null}
                      {isBioOfficer && b.status === 'SCHEDULED' ? (
                        outcomeFor === b.id ? (
                          <div className="mt-2 space-y-2">
                            <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as 'PASS' | 'FAIL')}>
                              <option value="PASS">PASS</option>
                              <option value="FAIL">FAIL</option>
                            </select>
                            <textarea className="field-input" rows={2} placeholder="Findings" value={findings} onChange={(e) => setFindings(e.target.value)} />
                            <div className="flex gap-2">
                              <button className="btn-secondary" onClick={() => setOutcomeFor(null)}>
                                Cancel
                              </button>
                              <button className="btn-primary" disabled={busy || findings.trim().length < 3} onClick={() => completeBooking(b.id)}>
                                Record outcome
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex gap-2">
                            <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setOutcomeFor(b.id)}>
                              Record inspection outcome
                            </button>
                            <button className="text-xs font-semibold text-rose-600 underline" onClick={() => cancelBooking(b.id)}>
                              Cancel booking
                            </button>
                          </div>
                        )
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {isBioOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showDecision ? (
                <div className="flex flex-wrap gap-2">
                  {p.status === 'RISK_ASSESSED' || p.status === 'QUARANTINE_CLEARED' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowDecision('APPROVE')}>
                        Approve
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('REJECT')}>
                        Reject
                      </button>
                      <button className="btn-secondary" disabled={busy} onClick={() => setShowDecision('HOLD')}>
                        Hold
                      </button>
                    </>
                  ) : null}
                  {p.status === 'ISSUED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowDecision('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                  {!['ISSUED', 'FEE_PENDING', 'REJECTED', 'REVOKED', 'WITHDRAWN', 'EXPIRED'].includes(p.status) ? (
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label={showDecision === 'APPROVE' ? 'Note (optional)' : 'Reason'}>
                    <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowDecision(null)}>
                      Cancel
                    </button>
                    <button className={showDecision === 'APPROVE' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={confirmDecision}>
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewPermitModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [applicantType, setApplicantType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [applicantId, setApplicantId] = useState('');
  const [importCategory, setImportCategory] = useState<(typeof IMPORT_CATEGORIES)[number]>('LIVE_PLANT');
  const [commodityDescription, setCommodityDescription] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purpose, setPurpose] = useState<'COMMERCIAL' | 'PERSONAL' | 'RESEARCH' | 'BREEDING'>('COMMERCIAL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/biosecurity-permits', {
        applicantType,
        applicantId,
        importCategory,
        commodityDescription,
        originCountry,
        quantity: quantity || undefined,
        purpose,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New bio-security import permit" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant type">
            <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
          <Field label={applicantType === 'ENTITY' ? 'Business ID' : 'NIN'}>
            <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Import category">
            <select className="field-input" value={importCategory} onChange={(e) => setImportCategory(e.target.value as (typeof IMPORT_CATEGORIES)[number])}>
              {IMPORT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Purpose">
            <select className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value as typeof purpose)}>
              {(['COMMERCIAL', 'PERSONAL', 'RESEARCH', 'BREEDING'] as const).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Origin country">
          <input className="field-input" value={originCountry} onChange={(e) => setOriginCountry(e.target.value)} placeholder="e.g. India" />
        </Field>
        <Field label="Commodity description">
          <textarea className="field-input" rows={2} value={commodityDescription} onChange={(e) => setCommodityDescription(e.target.value)} />
        </Field>
        <Field label="Quantity (optional)">
          <input className="field-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g. 200 seedlings" />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !applicantId || !originCountry || commodityDescription.length < 3} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit application (resolves risk matrix)'}
        </button>
      </div>
    </Modal>
  );
}

// ── Quarantine facilities ─────────────────────────────────────────────────

function FacilitiesTab() {
  const { persona } = useAuth();
  const isBioOfficer = persona ? persona.roles.includes('BIOSECURITY_OFFICER') : false;
  const [showCreate, setShowCreate] = useState(false);
  const listQ = useApiQuery<Page<QuarantineFacility>>('/api/v1/m33/quarantine-facilities?limit=100');

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {isBioOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New facility
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No quarantine facilities configured" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Kind</th>
                <th className="table-head">District</th>
                <th className="table-head">Active</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id}>
                  <td className="table-cell font-mono text-xs font-medium">{f.code}</td>
                  <td className="table-cell">{f.name}</td>
                  <td className="table-cell text-xs">{f.kind}</td>
                  <td className="table-cell text-xs text-slate-500">{f.district ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.active ? 'ACTIVE' : 'CLOSED'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <NewFacilityModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewFacilityModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<(typeof FACILITY_KINDS)[number]>('PLANT');
  const [district, setDistrict] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/quarantine-facilities', { code, name, kind, district: district || undefined });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New quarantine facility" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Code">
          <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="SEZ_PLANT_QF01" />
        </Field>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Kind">
          <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value as (typeof FACILITY_KINDS)[number])}>
            {FACILITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label="District (optional)">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !code || !name} onClick={submit}>
          {busy ? 'Creating…' : 'Create facility'}
        </button>
      </div>
    </Modal>
  );
}

// ── Risk matrix ────────────────────────────────────────────────────────────

function RiskRulesTab() {
  const { persona } = useAuth();
  const isBioOfficer = persona ? persona.roles.includes('BIOSECURITY_OFFICER') : false;
  const [category, setCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/biosecurity-risk-rules?limit=100${category ? `&importCategory=${category}` : ''}`, [category]);
  const listQ = useApiQuery<Page<BiosecurityRiskRule>>(path);

  const deactivate = async (id: string) => {
    await api.post(`/api/v1/m33/biosecurity-risk-rules/${id}/deactivate`);
    listQ.reload();
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {IMPORT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        {isBioOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New risk rule
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No risk rules configured" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Category</th>
                <th className="table-head">Origin (blank = default)</th>
                <th className="table-head">Risk</th>
                <th className="table-head">Notes</th>
                {isBioOfficer ? <th className="table-head"></th> : null}
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id}>
                  <td className="table-cell text-xs">{r.importCategory.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{r.originCountry ?? '— (default)'}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.riskCategory} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{r.notes ?? '—'}</td>
                  {isBioOfficer ? (
                    <td className="table-cell">
                      <button className="text-xs font-semibold text-rose-600 underline" onClick={() => deactivate(r.id)}>
                        Deactivate
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <NewRiskRuleModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewRiskRuleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [importCategory, setImportCategory] = useState<(typeof IMPORT_CATEGORIES)[number]>('LIVE_PLANT');
  const [originCountry, setOriginCountry] = useState('');
  const [riskCategory, setRiskCategory] = useState<(typeof RISK_CATEGORIES)[number]>('MEDIUM');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/biosecurity-risk-rules', {
        importCategory,
        originCountry: originCountry || null,
        riskCategory,
        notes: notes || undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New risk-matrix rule" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Import category">
          <select className="field-input" value={importCategory} onChange={(e) => setImportCategory(e.target.value as (typeof IMPORT_CATEGORIES)[number])}>
            {IMPORT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Origin country (leave blank for this category's default rule)">
          <input className="field-input" value={originCountry} onChange={(e) => setOriginCountry(e.target.value)} />
        </Field>
        <Field label="Risk category">
          <select className="field-input" value={riskCategory} onChange={(e) => setRiskCategory(e.target.value as (typeof RISK_CATEGORIES)[number])}>
            {RISK_CATEGORIES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create rule'}
        </button>
      </div>
    </Modal>
  );
}
